import { ref } from 'vue'
import { COPILOT_API_URL, copilotHeaders } from '@/config/copilot'

/**
 * Aprueba o rechaza un pipeline que quedó en espera de aprobación.
 * POST ${COPILOT_API_URL}/api/pipelines/:pipelineId/approve
 * body: { approved: boolean, reason?: string } (reason obligatorio cuando approved=false).
 * Header-only (Bearer idToken + X-Tenant-Id vía copilotHeaders). Fail-closed: propaga el error
 * (parseando { error } del cuerpo) para que el caller lo muestre.
 */
export function usePipelineApprovalApi() {
  const isSubmitting = ref(false)
  const error = ref<string | null>(null)

  const approvePipeline = async (
    pipelineId: string,
    approved: boolean,
    reason?: string,
  ): Promise<{ message?: string }> => {
    isSubmitting.value = true
    error.value = null
    try {
      const res = await fetch(`${COPILOT_API_URL}/api/pipelines/${encodeURIComponent(pipelineId)}/approve`, {
        method: 'POST',
        headers: await copilotHeaders(),
        body: JSON.stringify({ approved, reason }),
      })

      let body: { ok?: boolean; message?: string; error?: string } = {}
      try {
        body = (await res.json()) as typeof body
      } catch {
        body = {}
      }

      if (!res.ok || body.ok === false) {
        throw new Error(body.error || `HTTP ${res.status}`)
      }

      return { message: body.message }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Error al procesar la aprobación'
      throw err
    } finally {
      isSubmitting.value = false
    }
  }

  return { isSubmitting, error, approvePipeline }
}
